// Which id addresses a clinical record, and which id only matches a person in
// memory. TGCLOUD-1758.
//
// Two id spaces meet on a pedigree node and only one of them belongs in the
// database:
//
//   familyMemberId  a plaintext UPN, `<familyId>-<upn>`, e.g. 'G-1-01514-3'.
//                   Stable. Every reader keys on it — the record tiles, the
//                   record browser, the data overlays' person resolver, and the
//                   backend's own scope guard.
//   proBandId       the root's own id. Also stable, also a valid address.
//   pid             a canvas-local integer. NOT stable: `normalizeIds`
//                   renumbers pids on every load of the chart, and proxy clones
//                   mint synthetic ones.
//
// The sidebar used to fall back through all three when deciding what to store on
// a new record, so a record created against a member who had not been saved yet
// was written with a pid. It matched nothing, and could never be matched again
// even in principle: the id is from the wrong space AND the number it holds
// refers to a different person after the next load. The record existed in the
// collection attached to nobody. The data overlays report these as `unresolved`
// rather than dropping them, which is how the defect was found.
//
// It also went wrong in the other direction, because the tile filters compare
// loosely (`==`): a pid sitting in this slot could collect an already-orphaned
// record onto whichever member happens to hold that pid today, and show one
// person's clinical record under another person's name.
//
// So the persisted address excludes the pid, and an unsaved member yields the
// 'none' sentinel that already suppresses the create-record control. The backend
// refuses these writes too (a 400 from clinicalRecordWriteGuard), so this is
// about not walking a clinician into that error after they have filled a form
// in, rather than being the only line of defence.

export const NO_MEMBER = 'none';

// The id a stored clinical record is ADDRESSED by. Never a pid.
export const recordAddressFor = (data) =>
  data?.familyMemberId ?? data?.proBandId ?? NO_MEMBER;

// A person IS selected but has never been saved, so has no UPN yet.
//
// Distinct from "nothing is selected", which is the other way the address reads
// 'none' and which should render nothing at all rather than an explanation.
export const isUnsavedMember = (data) =>
  recordAddressFor(data) === NO_MEMBER && data?.pid != null;

// The id used to match a person against the IN-MEMORY member list.
//
// The pid stays in this chain deliberately — nothing here is persisted, and an
// unsaved member has no other handle, so dropping it would stop the Suggestions
// flow from applying to a member just added on the canvas. Stringified because a
// pid is a number while a UPN is a string.
export const memberMatchKeyFor = (member) =>
  String(member?.familyMemberId ?? member?.proBandId ?? member?.pid ?? '');

export const hydrateMember = (member, members) => {
  if (!member) return member;
  const list = Array.isArray(members) ? members : [];

  const byPid =
    member.pid == null
      ? undefined
      : list.find((m) => m?.pid != null && m.pid === member.pid);
  if (byPid) return { ...member, ...byPid };

  const key = memberMatchKeyFor(member);
  if (!key) return member;
  const byKey = list.find((m) => memberMatchKeyFor(m) === key);
  return byKey ? { ...member, ...byKey } : member;
};
